import { EventSource } from "eventsource";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useWorktreeStore } from "@/composables/worktree-manager";
import config from "@/config";
import { downloadBlob } from "@/lib/download";
import { api, type types } from "@/lib/openapi";
import { ThreadContextKind, ThreadKind } from "@/lib/openapi/gen";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { notifyEntityChanged } from "@/stores/cache-invalidation";

export type ThreadSchema = types.ThreadSchema;
export type ThreadEntrySchema = types.ThreadEntrySchema;
export type ThreadWithEntriesSchema = types.ThreadWithEntriesSchema;
export type ConflictRecordSchema = types.ConflictRecordSchema;
export type EntityVersionsSchema = types.EntityVersionsSchema;
export type UserChangeSchema = types.UserChangeSchema;
export type AffectedEntitySchema = types.AffectedEntitySchema;

export type ConflictResolution = "keep_main" | "keep_thread" | "edited";

export interface AffectedEntity {
    entity_type: string;
    entity_id: string;
}

/**
 * Outcome of an accept/reject. `conflict` means the accept was gated because the
 * thread is behind main: the backend recorded pick-a-side conflict records during
 * the forced catch-up, the active thread has been refetched so they're now on
 * `conflicts`, and the caller should show the resolution view instead of closing.
 */
export type ResolveOutcome = "resolved" | "conflict" | "error";

/**
 * The OpenAPI client is configured `throwOnError`, so a non-2xx response REJECTS
 * with the parsed error body rather than resolving. A backend DomainError
 * serialises as `{ detail: [{ type, message }] }`; a blocked accept on a
 * behind-main thread surfaces `ErrorType.CONFLICT` (`type === "CONFLICT"`).
 */
interface DomainErrorBody {
    detail: Array<{ type?: unknown; message?: unknown }>;
}

/** A proposal answered by something else, most often auto apply, 409s. */
const isAlreadyAnswered = (error: unknown): boolean =>
    typeof (error as { detail?: unknown })?.detail === "string" &&
    (error as { detail: string }).detail.includes("no longer open");

const asDomainErrorBody = (error: unknown): DomainErrorBody | undefined => {
    if (
        error != null &&
        typeof error === "object" &&
        Array.isArray((error as { detail?: unknown }).detail)
    ) {
        return error as DomainErrorBody;
    }
    return undefined;
};

const isConflictError = (error: unknown): boolean =>
    asDomainErrorBody(error)?.detail.some((d) => d.type === "CONFLICT") ??
    false;

const domainErrorMessage = (error: unknown): string | undefined => {
    const message = asDomainErrorBody(error)?.detail[0]?.message;
    return typeof message === "string" ? message : undefined;
};

export const useThreadsStore = defineStore("threads", () => {
    const toast = useToast();
    const threads = ref<ThreadSchema[]>([]);
    const activeThread = ref<ThreadSchema | undefined>(undefined);
    const activeEntries = ref<ThreadEntrySchema[]>([]);
    const isLoading = ref(false);
    const isFetching = ref(false);
    // The name of the file currently being uploaded, or null when idle — drives
    // the "Uploading …" chip in the composer's attachments strip.
    const uploadingFileName = ref<string | null>(null);

    // Driven by SSE agent_started / agent_finished events, and seeded from the thread's
    // has_pending_agent_run on every full fetch (initial load + reconnect catch-up).
    const isWaitingForAgent = ref(false);

    // Live progress for the current agent run: each tool the agent invokes emits an
    // `agent_progress` step. These are transient placeholders shown while waiting; they
    // are cleared on `agent_finished`, at which point the persisted activity entries
    // (fetched via fetchThread) replace them — so each action is shown once, never twice.
    const agentProgressSteps = ref<string[]>([]);
    const agentRunStartedAt = ref<number | null>(null);

    // The proposal whose Confirm/Cancel the user just clicked. Set synchronously on
    // click so its approval buttons hide immediately (in sync with the "agent is
    // working" indicator) instead of a network round-trip later; cleared if the
    // answer fails so the buttons reappear for a retry. A stale id after a
    // successful answer is harmless — that entry is never the last one again.
    const respondingProposalId = ref<string | null>(null);

    // --- Catch-up conflicts (pick-a-side resolution) ---
    // A thread that is behind main and collided on catch-up carries conflict records;
    // each names the precise entity that needs a pick-a-side resolution before accept
    // can unblock. Exposed off the active thread so the drawer banner + per-conflict
    // rows render straight from store state.
    const conflicts = computed<ConflictRecordSchema[]>(
        () => activeThread.value?.conflicts ?? []
    );
    const pendingConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "pending")
    );
    // Resolved-but-not-accepted records: decisions stay revertible until
    // Accept (869dyyzh9), so the drawer keeps a review path to the Reconcile
    // page as long as any exist.
    const resolvedConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "resolved")
    );
    const hasPendingConflicts = computed(
        () => pendingConflicts.value.length > 0
    );

    // --- Non-agent (user) branch edits ---
    // Entities a user changed directly on the thread branch (NOT the facilitator
    // agent). Surfaced in the resolve dialog so a reviewer sees the non-agent
    // footprint they are about to accept/reject. Lazily fetched (the detection is
    // gitaly-heavy) when the dialog opens, not on every thread poll.
    const userChanges = ref<UserChangeSchema[]>([]);

    // --- Real-time updates via SSE (replaces the old polling loop) ---
    let eventSource: EventSource | null = null;

    const lastSequence = (): number =>
        activeEntries.value.reduce(
            (max, e) => Math.max(max, e.sequence ?? 0),
            0
        );

    const disconnectThread = () => {
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
    };

    const connectToThread = (threadId: string) => {
        // Tear down any previous connection (e.g. switching threads).
        disconnectThread();

        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        const source = new EventSource(
            `${config.bekendURI}/thread/events/${threadId}`,
            {
                // Resolve the token *inside* the fetch override: the eventsource lib reuses
                // this closure on every auto-reconnect, and the SSE endpoint re-authorizes on
                // each reconnect. Firebase ID tokens expire ~1h, so capturing once would let
                // the stream silently 401 after expiry. getToken() returns a cached token and
                // auto-refreshes near expiry, so this stays cheap.
                fetch: async (input, init) => {
                    const token = await getToken();
                    return fetch(input, {
                        ...init,
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${token}`,
                            Organization: organization?.value?.identity ?? "",
                            Stek: stek?.value?.identity ?? "",
                        },
                    });
                },
            }
        );
        eventSource = source;

        // Fires on initial connect and on every auto-reconnect — full fetch to catch up
        // on anything missed while disconnected (pub/sub is fire-and-forget).
        source.addEventListener("open", () => {
            void fetchThread(threadId);
        });

        source.addEventListener("message", ({ data }) => {
            let event: { type?: string };
            try {
                event = JSON.parse(data);
            } catch {
                return;
            }
            switch (event.type) {
                case "new_entry":
                    void fetchNewEntries(threadId);
                    break;
                case "agent_started":
                    isWaitingForAgent.value = true;
                    agentProgressSteps.value = [];
                    // Don't reset the elapsed clock if it's already running for this
                    // run (an optimistic set may have started it at click time);
                    // agent_finished clears it.
                    if (agentRunStartedAt.value === null) {
                        agentRunStartedAt.value = Date.now();
                    }
                    break;
                case "agent_progress": {
                    const message = (event as { message?: string }).message;
                    if (message) {
                        agentProgressSteps.value = [
                            ...agentProgressSteps.value,
                            message,
                        ];
                    }
                    break;
                }
                case "agent_finished":
                    isWaitingForAgent.value = false;
                    // Clear the live steps: the persisted activity entries fetched
                    // below replace them, so nothing is shown twice.
                    agentProgressSteps.value = [];
                    agentRunStartedAt.value = null;
                    // The agent may have committed entity changes on the branch at
                    // the SAME thread/<id> revision. Re-sync the worktree (diff
                    // badges), then refetch the thread for authoritative
                    // affected_entities and notify the entity bus so the viewed
                    // entity (e.g. a process's BPMN definition) refetches — a
                    // same-revision commit is invisible to the revision-keyed fetch
                    // otherwise, so it would stay stale until a manual refresh.
                    void useWorktreeStore().sync();
                    void (async () => {
                        try {
                            await fetchThread(threadId);
                            const affected =
                                activeThread.value?.affected_entities ?? [];
                            if (affected.length > 0) {
                                notifyEntityChanged(
                                    affected.map((e) => ({
                                        entityType: e.entity_type,
                                        entityId: e.entity_id,
                                    }))
                                );
                            }
                        } catch {
                            // Best-effort; the worktree sync above already
                            // refreshed the diff badges.
                        }
                    })();
                    break;
                case "thread_resolved":
                    void fetchThread(threadId);
                    void fetchThreads();
                    disconnectThread();
                    break;
            }
        });

        // EventSource auto-reconnects on transient errors (and 'open' re-fires, triggering a
        // catch-up fetch), but it does NOT reconnect on a fatal response such as a 403 — so we
        // log without claiming a retry, making a permanent failure visible rather than letting
        // updates silently stall. (A user-facing reconnecting/disconnected indicator that can
        // distinguish the two states would be a good follow-up.)
        source.addEventListener("error", () => {
            console.warn(
                `Thread SSE connection error (thread ${threadId}); ` +
                    `the browser reconnects if it was transient.`
            );
        });
    };

    /**
     * Map from entity identity to the open threads that affect it.
     * Only includes open threads.
     */
    const entityThreadMap = computed(() => {
        const map = new Map<string, ThreadSchema[]>();
        for (const thread of threads.value) {
            if (thread.status !== "open") continue;
            const affected =
                (
                    thread as ThreadSchema & {
                        affected_entities?: AffectedEntity[];
                    }
                ).affected_entities ?? [];
            for (const ae of affected) {
                const existing = map.get(ae.entity_id) ?? [];
                existing.push(thread);
                map.set(ae.entity_id, existing);
            }
        }
        return map;
    });

    const getThreadsForEntity = (entityId: string): ThreadSchema[] => {
        return entityThreadMap.value.get(entityId) ?? [];
    };

    const hasThreadsForEntity = (entityId: string): boolean => {
        return entityThreadMap.value.has(entityId);
    };

    // Drawer state
    const drawerOpen = ref(false);
    const drawerThreadId = ref<string | undefined>(undefined);

    const openDrawer = (threadId?: string, thread?: ThreadSchema) => {
        drawerOpen.value = true;
        if (threadId) {
            drawerThreadId.value = threadId;
        }
        // Hydrate the drawer shell from a thread we already hold (e.g. one we just
        // created) so its header renders instantly instead of waiting on a fetch;
        // entries stream in via the SSE fetch that opening the drawer triggers.
        if (thread && thread.identity === threadId) {
            activeThread.value = thread;
            activeEntries.value = [];
        }
    };

    /**
     * Open the drawer on the threads *list* (no thread selected). Used by the
     * entity discussion indicators: clicking one shows the list of threads
     * rather than jumping straight into a thread's branch. Clears any
     * previously selected thread so a stale `drawerThreadId` doesn't reopen the
     * detail view; the active branch view (if any) is left untouched.
     */
    const openThreadsList = () => {
        drawerThreadId.value = undefined;
        drawerOpen.value = true;
    };

    const closeDrawer = () => {
        drawerOpen.value = false;
    };

    const selectThread = (threadId: string | undefined) => {
        drawerThreadId.value = threadId;
    };

    const fetchThreads = async () => {
        isFetching.value = true;
        const { data, response } =
            await api.startThreadThreadMgtListThreadsStartPost({});

        if (response.ok && data) {
            threads.value = data.threads;
        } else {
            toast.add({
                severity: "error",
                summary: "Could not load threads",
                detail: "Server returned " + response.status,
            });
        }
        isFetching.value = false;
    };

    const fetchThread = async (threadId: string) => {
        isFetching.value = true;
        const { data, response } =
            await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                },
            });

        if (response.ok && data) {
            activeThread.value = data.thread;
            activeEntries.value = data.entries ?? [];
            // Seed the agent indicator AND the in-flight run's replay (start time +
            // steps, recorded server-side) so opening or reconnecting mid-run shows
            // the current activity immediately; live SSE events append on top. The
            // server stores each step before publishing it, so its list is never
            // behind what SSE has delivered — re-seeding here can't drop a step.
            isWaitingForAgent.value =
                data.thread?.has_pending_agent_run ?? false;
            agentProgressSteps.value = data.thread?.agent_progress_steps ?? [];
            // Adopt the server's run-start only when we aren't already timing this
            // run locally, so a mid-run fetch (reconnect, catch-up) can't reset the
            // elapsed clock. Clear it when the server reports no active run.
            const serverRunStartedAt =
                data.thread?.agent_run_started_at ?? null;
            if (serverRunStartedAt === null) {
                agentRunStartedAt.value = null;
            } else if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = serverRunStartedAt;
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Could not load thread",
                detail: "Server returned " + response.status,
            });
        }
        isFetching.value = false;
    };

    /**
     * Read-only fetch of a thread's metadata (title, status, affected entities)
     * WITHOUT touching `activeThread`/`activeEntries` or the drawer. Used by the
     * history timeline to lazily enrich a decision entry when it's expanded, so
     * opening the history tab never disturbs whatever thread the drawer has open.
     * Returns null (silently — enrichment is best-effort) on any failure.
     */
    const fetchThreadSummary = async (
        threadId: string
    ): Promise<ThreadSchema | null> => {
        try {
            const { data, response } =
                await api.startThreadThreadMgtGetThreadStartPost({
                    body: { thread_id: threadId, include_entries: false },
                });
            if (response.ok && data) return data.thread;
        } catch {
            // Best-effort: the row falls back to the git-only decision data.
        }
        return null;
    };

    /**
     * Delta fetch: pull only entries newer than what we already have and append them.
     * Triggered by a `new_entry` SSE notification — keeps bandwidth proportional to new
     * activity instead of refetching the whole thread.
     */
    const fetchNewEntries = async (threadId: string) => {
        const { data, response } =
            await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                    since_sequence: lastSequence(),
                },
            });

        if (!response.ok || !data) return;
        // Ignore late deltas for a thread we've since navigated away from.
        if (activeThread.value?.identity !== threadId) return;

        activeThread.value = data.thread;
        const existing = new Set(activeEntries.value.map((e) => e.id));
        const fresh = (data.entries ?? []).filter((e) => !existing.has(e.id));
        if (fresh.length) {
            activeEntries.value = [...activeEntries.value, ...fresh];
        }
    };

    // Thread id -> the last catch-up's failure message. A failed comparison
    // with main means the recorded conflict set cannot be trusted (often:
    // NONE recorded) — accept surfaces read this to stay blocked instead of
    // presenting "nothing to resolve" and failing at the accept click.
    const catchUpFailures = ref<Record<string, string>>({});

    /**
     * Catch the thread's branch up with main — the open/view interaction trigger
     * (reconciliation model §4). Merges current master into the branch so a forked
     * domain reads fresh and the diff badges are accurate, and a blocked catch-up
     * records conflicts (surfaced via the behind-main banner). Best-effort: a
     * failure must never block viewing the thread, so it never throws — but it
     * is RECORDED (``catchUpFailures``) so accept surfaces can block on it. On
     * success the thread is refetched (to surface any newly-recorded conflicts) and
     * the worktree re-synced (to refresh the diff badges) so the view reflects the
     * post-catch-up state.
     */
    // The generated client's error body is {detail: [{type, message}]} — pull
    // the human message out of whatever shape arrives (a plain Error, a string,
    // the API detail list) so the banner never reads "[object Object]".
    const failureMessage = (reason: unknown): string => {
        if (typeof reason === "string") return reason;
        const detail = (reason as { detail?: { message?: string }[] } | null)
            ?.detail;
        if (Array.isArray(detail)) {
            const messages = detail
                .map((entry) => entry?.message)
                .filter(Boolean);
            if (messages.length) return messages.join("; ");
        }
        const direct = (reason as { message?: string } | null)?.message;
        if (direct) return direct;
        try {
            return JSON.stringify(reason) ?? String(reason);
        } catch {
            return String(reason);
        }
    };

    // The thread's status as far as this store already knows it — no fetch.
    const knownThreadStatus = (threadId: string): string | undefined => {
        if (activeThread.value?.identity === threadId) {
            return activeThread.value.status;
        }
        return threads.value.find((t) => t.identity === threadId)?.status;
    };

    const catchUpThread = async (threadId: string) => {
        // A closed thread has nothing to catch up — viewing an accepted or
        // rejected thread's page must not fire a doomed request (nor paint
        // its refusal as a comparison failure).
        const known = knownThreadStatus(threadId);
        if (known && known !== "open") return;

        const recordFailure = async (reason: unknown) => {
            // The backend refusing because the thread is no longer OPEN is
            // that same benign case, just discovered late (this view had no
            // status yet) — probe read-only and stay silent for closed ones.
            const summary = await fetchThreadSummary(threadId);
            if (summary && summary.status !== "open") return;
            catchUpFailures.value = {
                ...catchUpFailures.value,
                [threadId]: failureMessage(reason),
            };
            console.warn(
                `Could not catch thread ${threadId} up with main`,
                reason
            );
        };
        try {
            const { response, error } =
                await api.startThreadThreadMgtCatchUpStartPost({
                    body: { thread_id: threadId },
                });
            if (!response.ok) {
                await recordFailure(error);
                return;
            }
        } catch (error) {
            await recordFailure(error);
            return;
        }
        if (threadId in catchUpFailures.value) {
            const { [threadId]: _cleared, ...rest } = catchUpFailures.value;
            catchUpFailures.value = rest;
        }
        // Refresh unless the user has since switched to a different thread (don't
        // clobber their current view); refetch on first open (activeThread unset).
        if (!activeThread.value || activeThread.value.identity === threadId) {
            await fetchThread(threadId);
        }
        void useWorktreeStore().sync();
    };

    /**
     * Answer a proposal. Confirm runs the saved plan right away on the backend
     * (falls back to an agent run if it can't); Cancel just records the answer.
     */
    const respondToProposal = async (
        threadId: string,
        entryId: string,
        response: "confirm" | "cancel"
    ) => {
        // Hide this proposal's approval buttons immediately on click, in sync with
        // the indicator below, rather than a round-trip later when the answer entry
        // lands.
        respondingProposalId.value = entryId;
        // Confirming runs the proposed actions as an agent turn. Show the waiting
        // indicator immediately — before the agent_started SSE arrives — so
        // Confirm has feedback until the change lands; agent_finished clears it.
        if (response === "confirm") {
            isWaitingForAgent.value = true;
            agentProgressSteps.value = [];
            if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = Date.now();
            }
        }
        try {
            await api.startThreadThreadMgtRespondToProposalStartPost({
                body: {
                    thread_id: threadId,
                    entry_id: entryId,
                    response,
                },
            });
        } catch (error) {
            // Don't leave a stuck spinner if the proposal couldn't be answered, and
            // bring the approval buttons back so the user can retry.
            if (response === "confirm") {
                isWaitingForAgent.value = false;
            }
            respondingProposalId.value = null;
            // Auto apply can answer the proposal between render and click. That
            // is the outcome the user wanted, so refresh quietly.
            if (!isAlreadyAnswered(error)) {
                toast.add({
                    severity: "error",
                    summary: "Could not answer the proposal",
                    detail: "It may already be answered — refreshing.",
                    life: 4000,
                });
            }
        }
        await fetchThread(threadId);
    };

    /** Turn auto apply on or off for the open thread. */
    const setAutoApply = async (enabled: boolean) => {
        const threadId = activeThread.value?.identity;
        if (!threadId) return;
        try {
            await api.startThreadThreadMgtSetAutoApplyStartPost({
                body: { thread_id: threadId, enabled },
            });
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not change auto mode",
                life: 4000,
            });
        }
        await fetchThread(threadId);
    };

    // Best-effort "user is typing" ping so the agent holds off while composing.
    const notifyTyping = async (threadId: string) => {
        try {
            await api.notifyTypingThreadTypingThreadIdPost({
                path: { thread_id: threadId },
            });
        } catch (e) {
            console.warn(`Typing ping failed for thread ${threadId}`, e);
        }
    };

    const createThread = async (
        title: string,
        description?: string,
        kind?: types.ThreadKind,
        subjectEntityType?: string,
        subjectEntityId?: string,
        contextItems?: types.ThreadContextItem[]
    ) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtCreateThreadStartPost({
                body: {
                    title,
                    description: description ?? null,
                    kind: kind ?? null,
                    subject_entity_type: subjectEntityType ?? null,
                    subject_entity_id: subjectEntityId ?? null,
                    context_items: contextItems ?? null,
                },
            });

        if (response.ok && data) {
            // Insert the created thread locally instead of refetching the whole
            // list. A full fetchThreads() recomputes affected_entities for every
            // open thread (an expensive per-thread VFS entity-path scan) and was
            // the main cost of thread-create latency. A brand-new thread affects
            // nothing yet, so the response is already complete.
            threads.value = [
                data,
                ...threads.value.filter((t) => t.identity !== data.identity),
            ];
            toast.add({
                severity: "success",
                summary: "Thread created",
                detail: data.title,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not create thread",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    // Creating a strategy-onboarding thread wakes the agent so it opens the chat.
    const startStrategyOnboarding = async () => {
        const data = await createThread(
            "Draft Vision & Mission",
            "Set up our Vision & Mission living document with the assistant.",
            ThreadKind.STRATEGY_ONBOARDING
        );
        if (data?.identity) {
            openDrawer(data.identity);
        }
        return data;
    };

    // The brief becomes the thread description, which is the opening
    // contribution the agent answers. Passing a kind wakes the agent at once.
    const startBusinessOnboarding = async (
        brief: string,
        contextItems?: types.ThreadContextItem[]
    ) => {
        const data = await createThread(
            "Set up my business",
            brief,
            ThreadKind.BUSINESS_ONBOARDING,
            undefined,
            undefined,
            contextItems
        );
        if (data?.identity) {
            openDrawer(data.identity);
        }
        return data;
    };

    /**
     * Thread whose findings should open as soon as they land. Set only for a run
     * the user started by hand, never for a weekly scan.
     */
    const pendingFindingsThreadId = ref<string | undefined>(undefined);

    const clearPendingFindings = () => {
        pendingFindingsThreadId.value = undefined;
    };

    const startMarketResearch = async () => {
        const data = await createThread(
            "Research our market",
            "Map our competitors and market trends with the assistant.",
            ThreadKind.MARKET_RESEARCH
        );
        if (data?.identity) {
            pendingFindingsThreadId.value = data.identity;
            openDrawer(data.identity);
        }
        return data;
    };

    const contribute = async (threadId: string, content: string) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtContributeToThreadStartPost({
                body: {
                    thread_id: threadId,
                    content,
                    source_type: "user",
                },
            });

        if (response.ok && data) {
            await fetchThread(threadId);
            // A contribution dispatches the agent, so show "agent is working"
            // immediately instead of waiting for the agent_started SSE (agent_finished
            // clears it). Set after the fetch so the optimistic flag wins; generic
            // across every thread kind. Steps then stream in from the replay + SSE.
            isWaitingForAgent.value = true;
            if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = Date.now();
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Could not contribute",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    // Files attached to the active thread (a FILE context item per upload). Every
    // viewer sees them; the thread agent reads them as context.
    const attachedFiles = computed(() =>
        (activeThread.value?.context_items ?? []).filter(
            (item) => item.kind === ThreadContextKind.FILE
        )
    );

    // Upload a file to a thread. The bytes land in the thread's VFS tree and a
    // FILE context item is appended; refetch so it shows for the uploader (other
    // viewers pick it up on their next thread fetch).
    const uploadThreadFile = async (threadId: string, file: File) => {
        isLoading.value = true;
        uploadingFileName.value = file.name;
        try {
            const { data, response } =
                await api.startThreadThreadMgtUploadThreadFileStartPost({
                    body: { thread_id: threadId, file_: file },
                });
            if (response.ok && data) {
                await fetchThread(threadId);
                return data;
            }
            toast.add({
                severity: "error",
                summary: "Could not attach file",
                detail: "Server returned " + response.status,
            });
        } catch (e) {
            toast.add({
                severity: "error",
                summary: "Could not attach file",
                detail: String(e),
            });
        } finally {
            isLoading.value = false;
            uploadingFileName.value = null;
        }
    };

    // Types safe to render in a tab because they cannot execute script. A blob
    // URL inherits THIS app's origin, so opening a file the uploader marked
    // text/html or image/svg+xml would run their markup as us (reading the
    // session from localStorage). The stored mimetype is whatever the uploading
    // client claimed and is never validated server-side, so anything not on this
    // list is saved rather than rendered.
    const INLINE_SAFE_MIMETYPES = new Set([
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/gif",
        "image/webp",
        "image/bmp",
        "text/plain",
    ]);

    const baseMimetype = (blob: Blob): string =>
        (blob.type || "").split(";")[0].trim().toLowerCase();

    // Open a thread attachment. The API needs the auth/org/stek headers, so a
    // plain link can't reach it — fetch the bytes, then hand a blob URL to a new
    // tab. Only for types that cannot carry script; everything else is saved,
    // because a blob URL of attacker-chosen HTML/SVG would execute as our origin.
    // The tab is opened synchronously so pop-up blockers, which require a user
    // gesture, don't kill it.
    const openThreadFile = async (
        threadId: string,
        fileId: string,
        filename: string
    ) => {
        const tab = window.open("", "_blank");
        const { data, response } =
            await api.startThreadThreadMgtGetThreadFileStartPost({
                body: { thread_id: threadId, file_id: fileId },
                parseAs: "blob",
            });
        if (!response.ok || !data) {
            tab?.close();
            toast.add({
                severity: "error",
                summary: "Could not open file",
                detail: "Server returned " + response.status,
            });
            return;
        }
        const blob = data as Blob;
        if (!INLINE_SAFE_MIMETYPES.has(baseMimetype(blob))) {
            // Not safe to render inline — save it instead of executing it.
            tab?.close();
            downloadBlob(URL.createObjectURL(blob), filename);
            return;
        }
        const url = URL.createObjectURL(blob);
        if (tab) {
            tab.location.href = url;
            // Keep the object URL alive long enough for the tab to load it.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
            // Pop-up blocked — save it so the click isn't lost.
            downloadBlob(url, filename);
        }
    };

    // Save a thread attachment to disk. Same authenticated fetch as `open`, but
    // always handed to an <a download>, which writes the file rather than
    // rendering it — so the uploader's mimetype cannot execute here.
    const downloadThreadFile = async (
        threadId: string,
        fileId: string,
        filename: string
    ) => {
        const { data, response } =
            await api.startThreadThreadMgtGetThreadFileStartPost({
                body: { thread_id: threadId, file_id: fileId },
                parseAs: "blob",
            });
        if (!response.ok || !data) {
            toast.add({
                severity: "error",
                summary: "Could not download file",
                detail: "Server returned " + response.status,
            });
            return;
        }
        downloadBlob(URL.createObjectURL(data as Blob), filename);
    };

    // Remove an attachment from a thread. The server allows this only for the
    // user who uploaded it; refetch so the strip drops the chip on success.
    const deleteThreadFile = async (threadId: string, fileId: string) => {
        try {
            const { response } =
                await api.startThreadThreadMgtDeleteThreadFileStartPost({
                    body: { thread_id: threadId, file_id: fileId },
                });
            if (response.ok) {
                await fetchThread(threadId);
                return;
            }
            toast.add({
                severity: "error",
                summary: "Could not remove file",
                detail: "Server returned " + response.status,
            });
        } catch (e) {
            toast.add({
                severity: "error",
                summary: "Could not remove file",
                detail: String(e),
            });
        }
    };

    // Creating a process-BPMN thread binds it to the process and wakes the agent
    // to model it from the user's description.
    const startProcessBpmnThread = async (
        processId: string,
        processName: string,
        description: string,
        onCreated?: () => void
    ) => {
        // Do the slow work while the launching dialog is still up: create the
        // thread, then post the description (which dispatches the agent and marks it
        // active server-side). Only THEN close the dialog (onCreated) and open the
        // drawer, so it reveals a thread that already has the description and the
        // agent working — no post-open flicker of the indicator. The drawer opens
        // WITHOUT hydrate data because `contribute` already fetched the full, current
        // thread into activeThread/activeEntries.
        const data = await createThread(
            `Build BPMN for ${processName}`,
            undefined,
            ThreadKind.PROCESS_BPMN,
            "process",
            processId,
            [
                {
                    kind: ThreadContextKind.ENTITY,
                    data: { entity_type: "process", entity_id: processId },
                },
            ]
        );
        if (data?.identity) {
            await contribute(data.identity, description);
            onCreated?.();
            openDrawer(data.identity);
        }
        return data;
    };

    const resolveThread = async (
        threadId: string,
        action: "accept" | "reject",
        reason: string,
        why?: string
    ): Promise<ResolveOutcome> => {
        isLoading.value = true;
        try {
            await api.startThreadThreadMgtResolveThreadStartPost({
                body: {
                    thread_id: threadId,
                    action,
                    reason,
                    // Accept-only rationale (the "why" behind the change). For a
                    // reject the reason already carries the full explanation.
                    why: action === "accept" ? why?.trim() || null : null,
                },
            });
            // Clean accept/reject: refresh the active thread + list so the UI
            // reflects the resolved status.
            await fetchThread(threadId);
            await fetchThreads();
            toast.add({
                severity: "success",
                summary: `Thread ${action}ed`,
                detail: reason,
                life: 3000,
            });
            return "resolved";
        } catch (error) {
            // A behind-main thread gates accept with a CONFLICT DomainError: the
            // backend already recorded the pick-a-side conflict records via the
            // forced catch-up, so refetch the thread to surface them (they ride on
            // `activeThread.conflicts` → the "Behind main" banner + resolution
            // panel) and route the caller into the resolution view. A reject's
            // partially-merged CONFLICT is NOT a pick-a-side case — fall through to
            // the error toast so it's surfaced rather than swallowed.
            if (action === "accept" && isConflictError(error)) {
                await fetchThread(threadId);
                return "conflict";
            }
            toast.add({
                severity: "error",
                summary: `Could not ${action} thread`,
                detail: domainErrorMessage(error) ?? "Unexpected error",
            });
            return "error";
        } finally {
            // Always clear loading — a thrown CONFLICT used to leave the spinner
            // hanging forever.
            isLoading.value = false;
        }
    };

    /**
     * Read a conflicted entity's content at base / master / branch for the
     * pick-a-side 3-way diff. Returns the three sides (or undefined on failure).
     */
    const readEntityVersions = async (
        threadId: string,
        entityType: string,
        entityId: string
    ): Promise<EntityVersionsSchema | undefined> => {
        const { data, response } =
            await api.startThreadThreadMgtReadEntityVersionsStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                },
            });

        if (response.ok && data) {
            return data;
        }
        toast.add({
            severity: "error",
            summary: "Could not load entity versions",
            detail: "Server returned " + response.status,
        });
        return undefined;
    };

    /**
     * Fetch the entities a user (not the agent) edited directly on the thread
     * branch, and cache them on `userChanges`. Called when the resolve dialog
     * opens so its "Changes made directly by users" section reflects the branch.
     * Returns the list (empty on failure or when there are no user edits).
     */
    const fetchUserChanges = async (
        threadId: string
    ): Promise<UserChangeSchema[]> => {
        const { data, response } =
            await api.startThreadThreadMgtListThreadUserChangesStartPost({
                body: { thread_id: threadId },
            });

        if (response.ok && data) {
            userChanges.value = data;
            return data;
        }
        userChanges.value = [];
        toast.add({
            severity: "error",
            summary: "Could not load user changes",
            detail: "Server returned " + response.status,
        });
        return [];
    };

    /**
     * Ask the backend for an LLM-suggested accept message built from the
     * thread's discussion + changes. Read-only: does not resolve the thread.
     * Returns null (and toasts) on failure so the caller can fall back to
     * manual entry — generation must never block accepting.
     */
    const generateAcceptMessage = async (
        threadId: string
    ): Promise<{ what: string; why: string } | null> => {
        try {
            const { data, response } =
                await api.startThreadThreadMgtGenerateAcceptMessageStartPost({
                    body: { thread_id: threadId },
                });
            if (response.ok && data) {
                return { what: data.what, why: data.why ?? "" };
            }
        } catch {
            // fall through to the toast + null
        }
        toast.add({
            severity: "error",
            summary: "Could not suggest a message",
            detail: "You can still write one manually.",
            life: 3000,
        });
        return null;
    };

    /**
     * Resolve one entity's catch-up conflict by picking a side (keep_main /
     * keep_thread / edited). Refetches the thread so the banner + per-conflict
     * rows reflect the new status (and accept unblocks once none stay pending).
     */
    const resolveConflict = async (
        threadId: string,
        entityType: string,
        entityId: string,
        resolution: ConflictResolution,
        content?: string
    ): Promise<ConflictRecordSchema | undefined> => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtResolveConflictStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                    resolution,
                    content: content ?? null,
                },
            });

        if (response.ok && data) {
            await fetchThread(threadId);
            // The branch entity now holds the chosen version (keep_main /
            // keep_thread / edited). Refresh the viewed entity + worktree so it
            // updates live on the branch — without this, the description stays on
            // the pre-resolution value until a manual page refresh.
            notifyEntityChanged([{ entityType, entityId }]);
            void useWorktreeStore().sync();
            toast.add({
                severity: "success",
                summary: "Conflict resolved",
                detail: data.name || entityId,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not resolve conflict",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return response.ok ? data : undefined;
    };

    return {
        threads,
        activeThread,
        activeEntries,
        conflicts,
        pendingConflicts,
        resolvedConflicts,
        hasPendingConflicts,
        userChanges,
        isLoading,
        isFetching,
        isWaitingForAgent,
        agentProgressSteps,
        agentRunStartedAt,
        respondingProposalId,
        drawerOpen,
        drawerThreadId,
        entityThreadMap,
        fetchThreads,
        fetchThread,
        fetchThreadSummary,
        fetchNewEntries,
        createThread,
        startStrategyOnboarding,
        startBusinessOnboarding,
        startMarketResearch,
        pendingFindingsThreadId,
        clearPendingFindings,
        startProcessBpmnThread,
        contribute,
        attachedFiles,
        uploadingFileName,
        uploadThreadFile,
        openThreadFile,
        downloadThreadFile,
        deleteThreadFile,
        respondToProposal,
        setAutoApply,
        notifyTyping,
        resolveThread,
        readEntityVersions,
        resolveConflict,
        fetchUserChanges,
        generateAcceptMessage,
        catchUpThread,
        catchUpFailures,
        connectToThread,
        disconnectThread,
        openDrawer,
        openThreadsList,
        closeDrawer,
        selectThread,
        getThreadsForEntity,
        hasThreadsForEntity,
    };
});

export const useThreadsManager = () => storeToRefs(useThreadsStore());
